'use client'

import React, { useState } from 'react'
import { GrowthBook, GrowthBookProvider as SdkProvider } from '@growthbook/growthbook-react'
import type { Experiment, Result } from '@growthbook/growthbook-react'
import { growthbookApiHost, growthbookClientKey } from './config'
import { getOrCreateDeviceId } from '../identity'
import { trackExperimentExposure } from './exposure-tracker'
import type { GrowthBookPayload } from './types'

/**
 * Client-side GrowthBook host, mounted around the homepage hero demo.
 *
 * **No flicker by construction.** The instance is built and hydrated inside a
 * `useState` initialiser, so it is fully populated *before* the first client
 * render — never a default-variant frame followed by a swap. That works because
 * the server already fetched the feature payload (see `growthbook/server.ts`)
 * and passes it in as a prop; `initSync` is a pure local operation with no
 * network call.
 *
 * **Exposure is not fired here.** Assignment happens lazily, when a consumer
 * actually evaluates a feature. No experiment is running right now (the hero
 * demo round-1 test concluded and its winner shipped unconditionally), so this
 * provider currently evaluates nothing — it stays mounted so the next
 * experiment only has to add its own consumer, not re-wire the SDK, the device
 * id, and the exposure fan-out. Whatever that consumer is, it should defer
 * evaluation until the tested UI is actually on screen: GrowthBook's exposure
 * count has to equal "people who could see it", which is the denominator the
 * conversion rate needs.
 */

export interface GrowthBookProviderProps {
  /** Feature definitions from the server; `null` when unconfigured or the CDN failed. */
  payload: GrowthBookPayload | null
  children: React.ReactNode
}

/**
 * Forward an experiment exposure into the existing analytics fan-out (GA4 +
 * Pixel). GrowthBook's own results are computed from this event in the
 * warehouse, so its shape is a contract — `experiment_id` / `variation_id` are
 * the field names GrowthBook's GA4 data source expects by default.
 */
function trackGrowthBookExposure(experiment: Experiment<unknown>, result: Result<unknown>): void {
  trackExperimentExposure(experiment.key, result.key)
}

export function GrowthBookProvider({ payload, children }: GrowthBookProviderProps) {
  const [gb] = useState(() => {
    const instance = new GrowthBook({
      apiHost: growthbookApiHost(),
      clientKey: growthbookClientKey(),
      // Bucketing is deterministic on this id, so a returning visitor keeps
      // their variant. Empty during SSR — harmless, because nothing evaluates
      // a feature until the browser mounts.
      attributes: { id: getOrCreateDeviceId() },
      trackingCallback: trackGrowthBookExposure,
    })

    if (payload) {
      // `streaming: false` on purpose: a live SSE channel would let a visitor's
      // variant change mid-session if someone edits the experiment, which
      // corrupts the very measurement we're running. Definitions are refreshed
      // on the next page load instead.
      instance.initSync({ payload, streaming: false })
    }

    return instance
  })

  return <SdkProvider growthbook={gb}>{children}</SdkProvider>
}
